import { Link } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import ArticleCard, { TypeTag } from "../components/ArticleCard";
import { Reveal, SectionHead } from "../components/Motion";
import { useFetch } from "../lib/useFetch";
import { asList } from "../lib/api";

function LeadStory({ lead, latest }) {
  return (
    <section className="px-5 lg:px-12 pt-24" data-testid="lead-section">
      <SectionHead eyebrow="The Lead" title="This week in BPJ" action={<Link to="/articles" className="hidden sm:flex items-center gap-2 text-sm font-semibold link-u" data-testid="lead-all-link">All articles <ArrowRight size={16} /></Link>} />
      <div className="grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-8"><ArticleCard a={lead} size="lg" /></Reveal>
        <div className="lg:col-span-4 lg:border-l rule lg:pl-10">
          <p className="eyebrow mb-6">Latest</p>
          <ol className="divide-y rule">
            {latest.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06} as="li" className="py-5 first:pt-0">
                <Link to={`/article/${a.slug}`} className="group flex gap-5" data-testid={`latest-${a.slug}`}>
                  <span className="f-serif text-3xl text-[var(--red)] leading-none w-6">{i + 1}</span>
                  <span>
                    <TypeTag type={a.type} />
                    <span className="block f-serif text-xl leading-snug mt-2 group-hover:text-[var(--red)] transition-colors">{a.title}</span>
                    <span className="block f-mono text-xs text-[var(--muted)] mt-2">{a.author}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function TwoTracks({ research, news }) {
  return (
    <section className="px-5 lg:px-12 pt-28" data-testid="tracks-section">
      <SectionHead eyebrow="Two ways to read" title={<>Research <em className="font-light">&</em> analysis</>} />
      <div className="grid md:grid-cols-2 gap-px bg-[var(--line)] border rule">
        {[["Peer-reviewed research", "Double-blind reviewed studies with DOIs, data and methods.", research, "research"], ["Business analysis", "Reported pieces translating evidence into decisions.", news, "news"]].map(([t, d, list, type]) => (
          <div key={type} className="bg-[var(--paper)] p-6 sm:p-10">
            <div className="flex items-start justify-between gap-4 mb-10">
              <div><h3 className="f-serif text-3xl">{t}</h3><p className="text-[var(--muted)] mt-2 max-w-sm">{d}</p></div>
              <Link to={`/articles?type=${type}`} className="shrink-0 w-11 h-11 border border-[var(--ink)] grid place-items-center hover:bg-[var(--ink)] hover:text-white transition-colors" data-testid={`tracks-more-${type}`} aria-label={`More ${t}`}><ArrowUpRight size={18} /></Link>
            </div>
            <div className="space-y-10">
              {list.slice(0, 3).map((a, i) => <Reveal key={a.slug} delay={i * 0.08}><ArticleCard a={a} size="sm" showImage={false} /></Reveal>)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function IssueSpotlight({ issues }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const clip = useTransform(scrollYProgress, [0, 0.4], ["inset(12% 18% 12% 18%)", "inset(0% 0% 0% 0%)"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const cur = issues[0];
  return (
    <section ref={ref} className="mt-32 bg-[#0D1B3E] text-white relative grain overflow-hidden" data-testid="issue-spotlight">
      <div className="px-5 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <p className="eyebrow !text-[#B5B5B5] mb-6">The magazine · {cur ? `${cur.season}` : ""}</p>
          <h2 className="f-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">{cur?.title || "The Productivity Paradox"}</h2>
          <p className="text-[#D6D6D6] text-lg mt-8 leading-relaxed max-w-md">{cur?.summary}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={`/reader/${cur?.id || "v14n4"}`} className="bg-white text-black h-12 px-6 inline-flex items-center gap-2 font-semibold hover:bg-[var(--red)] hover:text-white transition-colors" data-testid="spotlight-read-btn">Open the flip-book</Link>
            <Link to="/issues" className="border border-white/40 h-12 px-6 inline-flex items-center font-semibold hover:border-white transition-colors" data-testid="spotlight-archive-btn">Browse the archive</Link>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <motion.div style={{ clipPath: clip }} className="relative aspect-[3/2] overflow-hidden">
            <motion.img style={{ scale }} src="/img/product.jpg" alt="Stack of BPJ printed journals under a spotlight" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </div>
      <div className="px-5 lg:px-12 pb-24 grid grid-cols-2 sm:grid-cols-4 gap-5 lg:gap-8">
        {issues.slice(0, 4).map((is, i) => (
          <Reveal key={is.id} delay={i * 0.08}>
            <Link to={`/reader/${is.id}`} className="group block" data-testid={`spotlight-issue-${is.id}`}>
              <div className="aspect-[2/3] overflow-hidden bg-white/5"><img src={is.cover} alt={is.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:-translate-y-2 group-hover:scale-[1.03]" loading="lazy" /></div>
              <p className="f-mono text-xs text-[#B5B5B5] mt-4">Vol. {is.volume} No. {is.number}</p>
              <p className="f-serif text-lg mt-1">{is.title}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function CallForPapers() {
  return (
    <section className="px-5 lg:px-12 pt-32" data-testid="cfp-section">
      <div className="grid lg:grid-cols-12 gap-10 border-t border-[var(--ink)] pt-12">
        <Reveal className="lg:col-span-7">
          <p className="eyebrow !text-[var(--red)] mb-5">Call for papers · Open</p>
          <h2 className="f-serif text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">Have evidence that changes how firms perform? <em className="font-light">Publish it here.</em></h2>
        </Reveal>
        <Reveal className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end" delay={0.15}>
          <dl className="grid grid-cols-2 gap-6 mb-8">
            {[["6–8 wks", "First decision"], ["Double-blind", "Peer review"], ["Open", "Rolling submissions"], ["PDF / DOCX", "Up to 10 MB"]].map(([v, l]) => (
              <div key={l}><dt className="f-serif text-2xl">{v}</dt><dd className="eyebrow mt-1">{l}</dd></div>
            ))}
          </dl>
          <Link to="/submit" className="btn-red h-12 px-6 inline-flex items-center justify-center gap-2 font-semibold" data-testid="cfp-submit-btn">Submit a manuscript <ArrowRight size={18} /></Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { data: issues } = useFetch("/issues", []);
  const { data: articles } = useFetch("/articles", []);
  const list = asList(articles);
  const iss = asList(issues);
  const lead = list.find((a) => a.featured) || list[0];
  const rest = list.filter((a) => a !== lead);
  return (
    <div data-testid="home-page">
      <Hero issue={iss[0]} />
      <Marquee />
      {lead && <LeadStory lead={lead} latest={rest.slice(0, 5)} />}
      {list.length > 0 && <TwoTracks research={list.filter((a) => a.type === "research")} news={list.filter((a) => a.type === "news")} />}
      {iss.length > 0 && <IssueSpotlight issues={iss} />}
      <CallForPapers />
    </div>
  );
}
